import { Card, CardContent, CircularProgress, Dialog, DialogContent, Grid, Icon, Link, Stack, Tooltip } from '@mui/material'
import { STATUS } from 'common/constants/storeConstants'
import { CardTable } from 'common/components/datatable/Table'
import PropTypes from 'prop-types'
import DateTimePicker from 'common/components/form/DateTimePicker'
import Input from 'common/components/form/Input'
import ConfirmModal from 'common/components/modal/ConfirmModal'
import { BasicDialogAction, BasicDialogTitle } from 'common/components/modal/DialogComponents'
import ApplicationConf from 'conf/ApplicationConf'
import ProductFeatureSelect from 'gdl/commercial/product/components/ProductFeatureSelect'
import { LicensesActions } from 'gdl/licenses/LicensesSlice'
import { isNil, isNumber, orderBy } from 'lodash'
import React, { useEffect, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { useNavigate, useOutletContext, useParams } from 'react-router'
import useBoolean from 'common/hooks/useBoolean'
import useFetch from 'common/hooks/useFetch'
import useTitles from 'common/hooks/useTitles'
import { aquaFetch } from 'common/utils/ActionUtils'
import { getDate } from 'common/utils/DateUtil'
import { toast } from 'react-toastify'
import { fetchProducts } from '../../commercial/product/ProductsSlice'
import ModuleSynthesis from './ModuleSynthesis'
import { INSTANCE_TYPE } from 'supervision/instance/constants/InstanceConstants'
import { InstanceCard } from 'dashboard/components/KwaKeUpMap'
import { DISPLAY_MODE } from 'dashboard/utils/DashboardUtils'
import { CustomerTicketsStatsCard } from './CustomerTicketsStatsCard'
import { CardTitle } from '../../../common/components/card/Card'
import ContributorCard from './contributor/ContributorCard'
import CustomerCard from '../CustomerCard'
import NoteCard from '../../notes/NoteCard'
import useNoteTypeOptions from '../../notes/useNoteTypeOptions'
import moment from 'moment'
import { createNote, deleteNote, updateNote } from 'gdl/notes/NotesSlice'
import useUpdateEffect from 'common/hooks/useUpdateEffect'
import useActions from 'common/hooks/useActions'
import { DELETE, OTHER, SAVE } from 'common/components/action/ActionConstant'
import useCustomerFavorites from 'gdl/customers/useCustomerFavorites'
import useLocalStorage from 'common/hooks/useLocalStorage'
import { deleteCustomer, fetchCustomers, updateCustomer } from 'gdl/customers/CustomersSlice'
import { hasPermission } from 'common/utils/HabilitationUtil'
import CustomerInstanceDialog from './CustomerInstanceDialog'
import AssociateContactDialog from './contributor/AssociateContactDialog'
import ContactFormDialog from './contributor/ContactFormDialog'
import UserContractCard from './UserContractCard'
import ActiveUsersDialog from './ActiveUsersDialog'

const PopinEditProduct = ({
    isOpen = true,
    onClose = () => {},
    onValidate = () => {},
    selectedLicense,
}) => {
    const { commercialFeatures, productsById } = useSelector(store => ({
        commercialFeatures: store.gdl.commercialFeatures,
        productsById: store.gdl.products.byId,
    }), shallowEqual)
    const [license, setLicense] = useState(selectedLicense ?? {})

    // useEffect(() => {
    //     if (!isOpen) return
    //     setLicense(selectedLicense ?? {})
    // }, [isOpen, selectedLicense])

    const featuresIds = license.features?.map(l => l.commercialFeature) ?? []
    const product = productsById?.[license.product]

    return (
        <Dialog
            open={isOpen}
            maxWidth={'lg'}
        >
            <BasicDialogTitle onClose={onClose}>
                Modifier un produit
            </BasicDialogTitle>
            <DialogContent style={{ overflowX: 'hidden' }}>
                <Card>
                    <CardContent>
                        <Grid container columnSpacing={2} rowSpacing={1} alignItems='center' style={{ paddingTop: '5px' }}>
                            <Grid size={6}>
                                <Input
                                    label='Nom'
                                    value={license.name}
                                    onChange={v => setLicense(l => ({ ...l, name: v }))}
                                    disabled
                                />
                            </Grid>
                            <Grid size={6}>
                                <Input
                                    label='Prix de vente'
                                    value={license.salePrice}
                                    onChange={v => setLicense(l => ({ ...l, salePrice: v }))}
                                    type='float'
                                />
                            </Grid>
                            <Grid size={6}>
                                <DateTimePicker
                                    label='Date de début'
                                    value={license.startDate}
                                    onChange={v => setLicense(l => ({ ...l, startDate: v }))}
                                />
                            </Grid>
                            <Grid size={6}>
                                <DateTimePicker
                                    label='Date de fin'
                                    value={license.endDate}
                                    onChange={v => setLicense(l => ({ ...l, endDate: v }))}
                                />
                            </Grid>
                            <Grid size={12}>
                                <Input
                                    label='Description'
                                    value={license.description}
                                    onChange={v => setLicense(l => ({ ...l, description: v }))}
                                    multiline
                                />
                            </Grid>
                            <Grid size={12}>
                                <ProductFeatureSelect
                                    selectedIds={featuresIds}
                                    setSelectedIds={(ids) => {
                                        const features = ids.map(id => ({
                                            commercialFeature: id,
                                            price: commercialFeatures.byId[id]?.price,
                                        }))
                                        setLicense(l => ({ ...l, features }))
                                    }}
                                    product={product}
                                />
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            </DialogContent>
            <BasicDialogAction
                buttonLabel={isNil(selectedLicense) ? 'Créer' : 'Modifier'}
                onValidate={() => onValidate(license)}
                isDisabled={isNil(license.name) || isNil(license.startDate) || isNil(license.endDate)}
            />
        </Dialog>
    )
}

PopinEditProduct.propTypes = {
    isOpen: PropTypes.bool,
    onClose: PropTypes.func,
    onValidate: PropTypes.func,
    selectedLicense: PropTypes.shape({}),
}

const PopinAddProductToCustomer = ({
    isOpen = true,
    onClose = () => {},
    onValidate = () => {},
}) => {
    const { allProducts, commercialFeatures } = useSelector(store => ({
        allProducts: store.gdl.products.allProducts,
        commercialFeatures: store.gdl.commercialFeatures,
    }), shallowEqual)
    const [license, setLicense] = useState({})

    const formattedProducts = allProducts.map(p => ({
        product: p,
        name: p.name,
        modules: p.features.map(f => commercialFeatures.byId[f.idFeature]?.name).join(', '),
    }))

    const createLicenceWithProduct = selectedProduct => {
        onValidate({
            ...license,
            name: selectedProduct.name,
            product: selectedProduct.id,
            features: selectedProduct.features.filter(f => f.mode === 'FULL').map(f => ({
                licence: 0,
                commercialFeature: f.idFeature,
                price: commercialFeatures.byId[f.idFeature]?.price,
                updateDate: 0,
                updateLogin: '',
            })),
        })
    }

    return (
        <Dialog
            open={isOpen}
            maxWidth={'lg'}
        >
            <BasicDialogTitle onClose={onClose}>
                Ajout d'un produit
            </BasicDialogTitle>
            <DialogContent style={{ overflowX: 'hidden' }}>
                <Card>
                    <CardContent>
                        <Grid container columnSpacing={2} rowSpacing={1} alignItems='center' style={{ paddingTop: '5px' }}>
                            <Grid size={6}>
                                <Input
                                    label='Description'
                                    value={license.description}
                                    onChange={v => setLicense(l => ({ ...l, description: v }))}
                                    multiline
                                />
                            </Grid>
                            <Grid size={6}>
                                <Input
                                    label='Prix de vente'
                                    value={license.salePrice}
                                    onChange={v => setLicense(l => ({ ...l, salePrice: v }))}
                                    type='float'
                                />
                            </Grid>
                            <Grid size={6}>
                                <DateTimePicker
                                    label='Date de début'
                                    value={license.startDate}
                                    onChange={v => setLicense(l => ({ ...l, startDate: v }))}
                                    required
                                />
                            </Grid>
                            <Grid size={6}>
                                <DateTimePicker
                                    label='Date de fin'
                                    value={license.endDate}
                                    onChange={v => setLicense(l => ({ ...l, endDate: v }))}
                                    required
                                />
                            </Grid>
                            <Grid size={12}>
                                <CardTable
                                    title='Produits AQUASYS©'

                                    headers={[{ key: 'name', value: 'Nom' }, { key: 'modules', value: 'Modules' }]}
                                    rows={formattedProducts}
                                    onClickRow={v => {
                                        if (!license.startDate || !license.endDate) {
                                            toast.warning('Veuillez remplir les champs obligatoires')
                                        } else {
                                            createLicenceWithProduct(v.product)
                                        }
                                    }}
                                />
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            </DialogContent>
        </Dialog>
    )
}

PopinAddProductToCustomer.propTypes = {
    isOpen: PropTypes.bool,
    onClose: PropTypes.func,
    onValidate: PropTypes.func,
    selectedLicense: PropTypes.shape({}),
}

const LicensesCard = ({
    customerId,
}) => {
    const dispatch = useDispatch()
    const { commercialFeaturesById } = useSelector(store => ({
        commercialFeaturesById: store.gdl.commercialFeatures.byId,
    }), shallowEqual)

    const {
        value: isConfirmOpen,
        setTrue: onOpenConfirm,
        setFalse: onCloseConfirm,
    } = useBoolean(false)
    const {
        value: isOpen,
        setFalse: onClose,
        setTrue: onOpen,
    } = useBoolean(false)
    const [isNewProductOpen, setNewProductOpen] = useState(false)
    const [licenseId, setLicenseId] = useState()

    // Différé : on attend que la fiche soit chargée (customerId défini) avant les appels licences.
    useEffect(() => {
        if (!customerId) {
            return
        }
        dispatch(fetchProducts())
    }, [dispatch, customerId])

    const {
        data: licenses = [],
        reload,
        status = STATUS.IDLE,
    } = useFetch(customerId ? ApplicationConf.gdl.customerLicenses(customerId) : undefined)

    const formattedLicenses = licenses.map(l => ({
        name: { value: l.name, tooltip: l.description },
        startDate: getDate(l.startDate),
        endDate: getDate(l.endDate),
        salePrice: isNumber(l.salePrice) ? `${l.salePrice} €` : '',
        id: l.id,
        modules: l.features.map(f => commercialFeaturesById[f.commercialFeature]?.name).join(', '),
    }))

    const selectedLicense = licenses.find(l => l.id === licenseId)

    const onValidate = newLicense => {
        const formattedFeatures = newLicense.features?.map(feature => ({
            licence: newLicense.id ?? -1,
            updateLogin: '',
            updateDate: 0,
            ...feature,
        }))
        const formattedLicense = {
            id: -1,
            customer: parseInt(customerId),
            creationDate: 0,
            updateLogin: '',
            updateDate: 0,
            ...newLicense,
            features: formattedFeatures ?? [],
        }
        if (isNil(selectedLicense)) {
            dispatch(LicensesActions.create(formattedLicense)).then(r => {
                reload()
                setLicenseId(r.payload.id)
                onOpen()
            })
        } else {
            dispatch(LicensesActions.update(formattedLicense)).finally(() => {
                reload()
                setLicenseId()
                onClose()
            })
        }
    }

    return (
        <>
            <CardTable
                title='Produits'
                rows={formattedLicenses}
                headers={[{ key: 'name', value: 'Nom' }, { key: 'startDate', value: 'Date de début' }, { key: 'endDate', value: 'Date de fin' }, { key: 'salePrice', value: 'Prix' }, { key: 'modules', value: 'Modules' }]}
                status={status}
                lineActions={[{
                    icon: 'delete',
                    onClick: ({ id }) => {
                        onOpenConfirm()
                        setLicenseId(id)
                    },
                }, {
                    icon: 'edit',
                    onClick: ({ id }) => {
                        onOpen()
                        setLicenseId(id)
                    },
                }]}
                actions={[{
                    icon: 'add',
                    onClick: () => setNewProductOpen(true),
                    tooltip: 'Ajouter un produit',
                }, {
                    icon: 'replay',
                    onClick: reload,
                    tooltip: 'Recharger',
                }]}
            />
            {
                isNewProductOpen && (
                    <PopinAddProductToCustomer
                        isOpen={isNewProductOpen}
                        onClose={() => setNewProductOpen(false)}
                        onValidate={v => {
                            setNewProductOpen(false)
                            onValidate(v)
                        }}
                    />
                )
            }
            {
                isOpen && (
                    <PopinEditProduct
                        isOpen={isOpen}
                        onClose={() => {
                            onClose()
                            setLicenseId()
                        }}
                        onValidate={onValidate}
                        selectedLicense={selectedLicense}
                    />
                )
            }
            <ConfirmModal
                isOpen={isConfirmOpen}
                title='Êtes-vous sûr de vouloir supprimer cette licence ?'
                onValidate={() => {
                    dispatch(LicensesActions.delete(licenseId)).finally(() => {
                        reload()
                        setLicenseId()
                        onCloseConfirm()
                    })
                }}
                onClose={() => {
                    onCloseConfirm()
                    setLicenseId()
                }}
            />
        </>
    )
}

LicensesCard.propTypes = {
    customerId: PropTypes.number,
}

const NotesCard = ({
    customer,
}) => {
    const login = useSelector(store => store.app.user?.login, shallowEqual)
    const [openNotePopin, setOpenNotePopin] = useState(false)
    const [noteToEdit, setNoteToEdit] = useState({ creationDate: moment().valueOf(), creationLogin: login })
    const noteTypeOptions = useNoteTypeOptions()

    const { notes, reloadNotes } = useOutletContext()
    const dispatch = useDispatch()
    const navigate = useNavigate()

    const createdNote = () => {
        const note = { idClient: customer?.id, ...noteToEdit }
        dispatch(noteToEdit?.id ? updateNote(note) : createNote(note)).then(() => {
            setOpenNotePopin(false)
            setNoteToEdit({ creationDate: moment().valueOf(), creationLogin: login })
            reloadNotes()
        })
    }

    return (
        <>
            <Card>
                <CardTitle
                    title='3 Dernières notes'
                    actions={[{
                        icon: 'add',
                        onClick: () => setOpenNotePopin(true),
                        tooltip: 'Ajouter une note',
                    }, {
                        icon: 'more_horiz',
                        onClick: () => navigate(`/customer/${customer.id}/notes`),
                        tooltip: 'Voir toutes les notes',
                    }]}
                />
                <CardContent>
                    <Stack spacing={1}>
                        {orderBy(notes ?? [], ['updateDate', 'creationDate'], ['desc', 'desc'])?.slice(0, 3)?.map(note => (
                            <NoteCard
                                key={note.id}
                                note={note}
                                readMode
                                shortMode
                                contextActions={[{
                                    label: 'Modifier la note',
                                    onClick: () => {
                                        setNoteToEdit(note)
                                        setOpenNotePopin(true)
                                    },
                                    icon: 'edit',
                                }, {
                                    label: 'Supprimer la note',
                                    onClick: () => {
                                        dispatch(deleteNote(note.id)).then(() => {
                                            reloadNotes()
                                        })
                                    },
                                    icon: 'delete',
                                }]}
                            />
                        ))}
                    </Stack>
                </CardContent>
            </Card>
            {openNotePopin && (
                <Dialog
                    open={openNotePopin}
                    maxWidth='md'
                >
                    <BasicDialogTitle onClose={() => setOpenNotePopin(false)}>
                        Nouvelle note
                    </BasicDialogTitle>
                    <DialogContent>
                        <NoteCard
                            note={noteToEdit}
                            onChange={setNoteToEdit}
                            typeOptions={noteTypeOptions}
                        />
                    </DialogContent>
                    <BasicDialogAction
                        buttonLabel={'Valider'}
                        onValidate={createdNote}
                        isDisabled={!noteToEdit?.typeNote || !noteToEdit?.comment || !noteToEdit?.creationDate}
                    />
                </Dialog>
            )}
        </>
    )
}

NotesCard.propTypes = {
    customer: PropTypes.shape({
        id: PropTypes.number,
        notes: PropTypes.arrayOf(PropTypes.shape({})),
    }),
}

const CustomerDashboardApp = () => {
    const { id } = useParams()
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const { customer, reloadCustomer, projects = [] } = useOutletContext()
    const { isFavorite, toggleFavorite } = useCustomerFavorites()
    const fav = !isNil(customer) && isFavorite(customer.id)

    // Navigation précédent/suivant : ids de la liste (ordonnés) mémorisés par l'écran clients.
    const [navIds] = useLocalStorage('customers:navIds', [])
    const navIndex = (navIds ?? []).indexOf(Number(id))
    const prevCustomerId = navIndex > 0 ? navIds[navIndex - 1] : undefined
    const nextCustomerId = (navIndex >= 0 && navIndex < (navIds?.length ?? 0) - 1) ? navIds[navIndex + 1] : undefined

    // Vrai tant que le client affiché ne correspond pas à l'id de l'URL (données du client précédent
    // encore en place le temps que les appels se relancent) → on masque et on met un chargement.
    const isLoadingCustomer = isNil(customer) || customer.id !== Number(id)

    const {
        user,
        instances,
    } = useSelector(store => ({
        user: store.app.user,
        instances: store.supervision.instances,
    }), shallowEqual)

    const {
        value: isPopinOpen,
        setTrue: onOpenPopin,
        setFalse: onClosePopin,
    } = useBoolean(false)
    const {
        value: isConfirmOpen,
        setTrue: onOpenConfirm,
        setFalse: onCloseConfirm,
    } = useBoolean(false)

    const [selectedContrib, setSelectedContrib] = useState()
    const [popupContributorOpen, setPopupContributorOpen] = useState(false)
    const [editContact, setEditContact] = useState()

    const [selectedInstance, setSelectedInstance] = useState()

    const [customerState, setCustomerState] = useState(customer)
    const [customerInstances, setCustomerInstances] = useState(customer?.instances ?? [])
    const [customerContributors, setCustomerContributors] = useState(customer?.contributors ?? [])

    useUpdateEffect(() => {
        setCustomerState(customer)
        setCustomerInstances(customer?.instances ?? [])
        setCustomerContributors(customer?.contributors ?? [])
    }, [customer])

    useTitles(() => [{
        label: 'Clients',
        href: '/customer',
    }, {
        label: customer?.name ?? 'Inconnu',
        href: `/customer/${id}`,
    }, {
        label: 'Dashboard',
        href: `/customer/${id}/dashboard`,
    }], [id, customer])


    useActions(() => {
        // Navigation précédent/suivant : toujours disponible, même pendant le chargement d'un client.
        const navActions = [
            {
                type: OTHER,
                icon: 'arrow_drop_up',
                tooltip: 'Client précédent',
                displayed: !isNil(prevCustomerId),
                onClick: () => navigate(`/customer/${prevCustomerId}`),
            },
            {
                type: OTHER,
                icon: 'arrow_drop_down',
                tooltip: 'Client suivant',
                displayed: !isNil(nextCustomerId),
                onClick: () => navigate(`/customer/${nextCustomerId}`),
            },
        ]
        if (isLoadingCustomer) return navActions

        const shouldPulse = customerState?.name !== customer?.name
                         || customerState?.mnemonic !== customer?.mnemonic
                         || customerState?.description !== customer?.description
                         || customerState?.startDate !== customer?.startDate
                         || customerState?.endDate !== customer?.endDate
                         || customerState?.licenceActive !== customer?.licenceActive
                         || customerState?.vigilance !== customer?.vigilance
                         || customerState?.status !== customer?.status
                         || customerState?.maxAdmins !== customer?.maxAdmins
                         || customerState?.maxManagers !== customer?.maxManagers
                         || customerState?.maxConcurrentConnections !== customer?.maxConcurrentConnections
                         || customerInstances.length !== customer?.instances.length
                         || customerInstances.some(inst => !customer?.instances.some(i => i.instance === inst.instance && i.instanceType === inst.instanceType))
                         || customerContributors.length !== customer?.contributors.length
        //  || customerContributors.some(inst => !customer?.contributors.some(i => i.name === inst.name && i.type === inst.type))

        return [
            ...navActions,
            {
                type: OTHER,
                icon: fav ? 'star' : 'star_border',
                tooltip: fav ? 'Retirer des favoris' : 'Ajouter aux favoris',
                onClick: () => toggleFavorite(customer.id),
            },
            {
                type: OTHER,
                icon: 'undo',
                tooltip: 'Annuler les modifications',
                displayed: shouldPulse,
                onClick: () => {
                    setCustomerState(customer)
                    setCustomerInstances(customer?.instances ?? [])
                    setCustomerContributors(customer?.contributors ?? [])
                },
            },
            {
                type: SAVE,
                onClick: () => dispatch(updateCustomer({
                    id: customer.id,
                    name: customerState?.name,
                    mnemonic: customerState?.mnemonic,
                    description: customerState?.description,
                    startDate: customerState?.startDate,
                    endDate: customerState?.endDate,
                    licenceActive: customerState?.licenceActive,
                    vigilance: customerState?.vigilance,
                    status: customerState?.status,
                    maxAdmins: customerState?.maxAdmins,
                    maxManagers: customerState?.maxManagers,
                    maxConcurrentConnections: customerState?.maxConcurrentConnections,
                    commercialSensitivity: customerState?.commercialSensitivity,
                    nextDeadline: customerState?.nextDeadline,
                    creationDate: 0,
                    updateDate: 0,
                    updateLogin: '',
                    contributors: customerContributors,
                    instances: customerInstances,
                    notes: [],
                })).then(res => {
                    if (res.payload?.update > 0) {
                        reloadCustomer()
                        dispatch(fetchCustomers())
                    }
                }),
                pulse: shouldPulse,
                displayed: hasPermission(user, 'customers', 'update'),
            },
            {
                type: DELETE,
                onClick: () => dispatch(deleteCustomer(customer.id)).then(res => {
                    if (res.payload?.delete > 0) {
                        navigate('/customer')
                    }
                }),
                displayed: hasPermission(user, 'customers', 'delete'),
            },
        ]
    }, [customer, customerState, customerInstances, customerContributors, fav, prevCustomerId, nextCustomerId, isLoadingCustomer])

    // Différé : appel lancé une fois la fiche client chargée.
    const { data: activeUsersByInstance = [] } = useFetch(customer ? ApplicationConf.gdl.activeUsers() : undefined)

    // État des instances : données enrichies (statut back/db/front, jobs…) du dashboard supervision,
    // restreintes aux instances du client via l'endpoint dédié. Différé (gated sur customer).
    const { data: dashboardData = {} } = useFetch(
        customer?.instances?.length ? ApplicationConf.dashboardInstances() : undefined,
        { method: 'POST', body: { ids: (customer?.instances ?? []).map(ci => ci.instance) } },
    )
    const customerInstanceCards = (customer?.instances ?? []).map(ci => {
        const enriched = (dashboardData?.instances ?? []).find(di => di.instanceId === ci.instance)
        return enriched ? { enriched, services: (dashboardData?.services ?? []).filter(s => s.instance?.id === ci.instance) } : null
    }).filter(Boolean)
    const [selectedActiveUsers, setSelectedActiveUsers] = useState()

    // Détail par instance (fronts avec URL + paramètres, dont URL_SWAGGER) pour les colonnes du tableau.
    const [instanceDetails, setInstanceDetails] = useState({})
    useEffect(() => {
        setInstanceDetails({})
        ;(customer?.instances ?? []).forEach(ci => {
            aquaFetch(ApplicationConf.instance.single(ci.instance)).then(inst => {
                if (inst) {
                    setInstanceDetails(prev => ({ ...prev, [ci.instance]: inst }))
                }
            })
        })
    }, [customer])

    const formattedCustomerInstances = customerInstances.map(ci => {
        const name = instances.byId[ci.instance]?.name ?? 'Inconnue'
        const activeUsers = activeUsersByInstance.find(a => a.instance === ci.instance)
        const typeDef = INSTANCE_TYPE.find(t => t.value === instances.byId[ci.instance]?.instanceType)
        // Fronts (avec URL) + paramètres de l'instance -> icône cliquable par front / swagger.
        const detail = instanceDetails[ci.instance]
        const linkIcon = (url, icon) => (url ? (
            <Tooltip title='Cliquer pour ouvrir'>
                <Link component='button' onClick={() => window.open(url, '_blank')} sx={{ display: 'inline-flex' }}>
                    <Icon>{icon}</Icon>
                </Link>
            </Tooltip>
        ) : '')
        const frontIcon = (match, icon, title) => {
            const front = detail?.frontendVersions?.find(v => v.url && match((v.application ?? '').toLowerCase()))
            return linkIcon(front?.url, icon, front ? `${title} — ${front.application}` : title)
        }
        const swaggerUrl = detail?.parameters?.find(p => p.parameter === 'URL_SWAGGER')?.value
        return {
            instance: ci.instance,
            name,
            type: ci.instanceType,
            url: <Link target='_blank' href={instances.byId[ci.instance]?.url}>{instances.byId[ci.instance]?.url}</Link>,
            frontObservatoire: frontIcon(app => app.includes('observatoire'), 'public'),
            frontWidget: frontIcon(app => app.includes('widget'), 'widgets'),
            frontPortail: frontIcon(app => app.includes('portail'), 'yard'),
            frontSwagger: linkIcon(swaggerUrl, 'wifi', 'Swagger'),
            // Type produit de l'instance (SeQoya, YdilliQ…) avec la pastille de l'accueil.
            productType: { value: typeDef?.label ?? 'Inconnue' },
            activeUsers: isNil(activeUsers) || !activeUsers.reachable ? '' : (
                <Link
                    component='button'
                    onClick={() => setSelectedActiveUsers({ name, users: activeUsers.users })}
                >
                    {activeUsers.users.length}
                </Link>
            ),
        }
    })

    // Colonnes de fronts affichées uniquement si au moins une instance possède le front / le swagger.
    const instanceHeaders = [
        { key: 'name', value: 'Instance' },
        { key: 'type', value: 'Type d\'instance' },
        { key: 'url', value: 'URL' },
        ...(formattedCustomerInstances.some(r => r.frontObservatoire) ? [{ key: 'frontObservatoire', value: 'Observatoire', width: 90 }] : []),
        ...(formattedCustomerInstances.some(r => r.frontWidget) ? [{ key: 'frontWidget', value: 'Widget', width: 70 }] : []),
        ...(formattedCustomerInstances.some(r => r.frontPortail) ? [{ key: 'frontPortail', value: 'Portail', width: 70 }] : []),
        ...(formattedCustomerInstances.some(r => r.frontSwagger) ? [{ key: 'frontSwagger', value: 'Swagger', width: 70 }] : []),
        { key: 'productType', value: 'Type', width: 90 },
        { key: 'activeUsers', value: 'Utilisateurs actifs' },
    ]

    const onDeleteInstance = row => {
        setSelectedInstance(row.instance)
        onOpenConfirm()
    }

    const onEditInstance = row => {
        setSelectedInstance(row.instance)
        onOpenPopin()
    }

    const customerInstance = customerInstances.find(p => p.instance === selectedInstance)

    if (isLoadingCustomer) {
        return (
            <Grid container justifyContent='center' alignItems='center' sx={{ minHeight: '50vh' }}>
                <CircularProgress />
            </Grid>
        )
    }

    return (
        <Grid container spacing={1}>
            <Grid size={{ md: 12, lg: 9 }} spacing>
                <Stack spacing={1}>
                    <CustomerCard customer={customerState} onChange={setCustomerState} />
                    <UserContractCard customer={customerState} onChange={setCustomerState} />
                    <CardTable
                        title='Instances'
                        rows={formattedCustomerInstances}
                        headers={instanceHeaders}
                        actions={[{
                            icon: 'add',
                            onClick: onOpenPopin,
                            tooltip: 'Lier à une instance',
                        }]}
                        contextActions={(row) => [
                            { icon: 'info', label: 'Accéder à la fiche de l\'instance', onClick: () => navigate(`/instance/${row.instance}/dashboard`) },
                            { icon: 'edit', label: 'Modifier', onClick: () => onEditInstance(row) },
                            { icon: 'delete', label: 'Délier', color: 'red', onClick: () => onDeleteInstance(row) },
                        ]}
                    />
                    <LicensesCard customerId={customer?.id} />
                    <ModuleSynthesis customer={customer}/>
                </Stack>
            </Grid>
            <Grid size={{ md: 12, lg: 3 }}>
                <Stack spacing={1}>
                    <CustomerTicketsStatsCard customer={customer} projects={projects} />
                    {customerInstanceCards.length > 0 && (
                        <Card>
                            <CardTitle title='État des instances' />
                            <CardContent>
                                <Grid container spacing={1} sx={{ marginBottom: '5px' }}>
                                    {customerInstanceCards.map(({ enriched, services }) => (
                                        <Grid container size={{ xs: 12, sm: 6 }} key={enriched.instanceId}>
                                            <InstanceCard
                                                key={enriched.instanceId}
                                                instance={{ ...enriched, customer }}
                                                services={services}
                                                displayMode={DISPLAY_MODE.BACK}
                                                showAll
                                                frontLatestVersions={[]}
                                                onClick={() => navigate(`/instance/${enriched.instanceId}/dashboard`)}
                                            />
                                        </Grid>
                                    ))}
                                </Grid>
                            </CardContent>
                        </Card>
                    )}
                    <NotesCard customer={customer} />
                    <Card>
                        <CardTitle title='Intervenants'
                            actions={[{
                                icon: 'add',
                                onClick: () => setPopupContributorOpen(true),
                                tooltip: 'Ajouter un intervenant',
                            }]}
                        />
                        <CardContent>
                            <Stack spacing={1}>
                                {customerContributors?.map((contrib, idx) => (
                                    <ContributorCard
                                        key={contrib.name}
                                        contributor={contrib}
                                        onEdit={() => {
                                            setSelectedContrib({ ...contrib, idx })
                                            setPopupContributorOpen(true)
                                        }}
                                        onEditContact={() => setEditContact(contrib)}
                                        onDelete={() => setCustomerContributors(customerContributors.filter((c, idx2) => idx !== idx2))}
                                    />
                                ))}
                            </Stack>
                        </CardContent>
                    </Card>
                </Stack>
            </Grid>
            <CustomerInstanceDialog
                isOpen={isPopinOpen}
                customerInstance={customerInstance}
                onClose={() => {
                    setSelectedInstance()
                    onClosePopin()
                }}
                onValidate={newInst => {
                    const formattedParam = { ...newInst, customer: customer.id }
                    setCustomerInstances(insts => {
                        if (isNil(selectedInstance)) return [...insts, formattedParam]
                        return insts.map(p => p.instance === selectedInstance ? formattedParam : p)
                    })
                    setSelectedInstance()
                    onClosePopin()
                }}
            />
            <ActiveUsersDialog
                open={!isNil(selectedActiveUsers)}
                instanceName={selectedActiveUsers?.name ?? ''}
                users={selectedActiveUsers?.users ?? []}
                onClose={() => setSelectedActiveUsers()}
            />
            <ConfirmModal
                isOpen={isConfirmOpen}
                title='Êtes-vous sûr de vouloir supprimer cette liaison ?'
                onValidate={() => {
                    setCustomerInstances(insts => insts.filter(i => i.instance !== selectedInstance))
                    onCloseConfirm()
                }}
                onClose={onCloseConfirm}
            />

            {popupContributorOpen && (
                <AssociateContactDialog
                    contributor={selectedContrib}
                    onClose={() => {
                        setPopupContributorOpen(false)
                        setSelectedContrib()
                    }}
                    onValidate={contrib => {
                        const enriched = { ...contrib, updateDate: moment().valueOf(), updateLogin: user?.login, customer: customer.id }
                        if (selectedContrib?.idx || selectedContrib?.idx === 0) { // modif
                            setCustomerContributors(customerContributors.with(selectedContrib.idx, enriched))
                        } else { // ajout
                            setCustomerContributors([...customerContributors, enriched])
                        }
                        setSelectedContrib()
                        setPopupContributorOpen(false)
                    }}
                />
            )}
            {!isNil(editContact) && (
                <ContactFormDialog
                    contact={editContact}
                    onClose={() => setEditContact()}
                    onSaved={(saved) => {
                        // Le contact est partagé : on répercute les infos sur tous les intervenants qui le référencent.
                        setCustomerContributors(prev => prev.map(c => (
                            c.idContact === saved.idContact
                                ? { ...c, name: saved.name, email: saved.email, telephone: saved.telephone, description: saved.description, login: saved.login }
                                : c
                        )))
                    }}
                />
            )}
        </Grid>
    )
}

export default CustomerDashboardApp